import { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';

import {
  linkAttributeToProject,
  listAvailableAttributes,
  listProjectAttributes,
  unlinkAttributeFromProject,
} from './attributes.api';
import type { AttributeDefinition } from './attributes.types';

/**
 * Custom-field columns of one project. The visible set is the server's linked
 * list for this project (never local state): adding a field calls the link
 * route, removing it calls the unlink route, and a reload reads the same list.
 * A change is applied only after the server accepts it.
 */
export function useProjectAttributeColumns(
  workspaceId: string | null | undefined,
  projectId: string | null | undefined,
): {
  available: AttributeDefinition[];
  visibleIds: Set<string>;
  visibleDefinitions: AttributeDefinition[];
  loadFailed: boolean;
  toggleColumn: (definitionId: string, visible: boolean) => Promise<void>;
  onCreated: (definition: AttributeDefinition) => Promise<void>;
  retry: () => void;
} {
  const [available, setAvailable] = useState<AttributeDefinition[]>([]);
  const [linkedIds, setLinkedIds] = useState<Set<string>>(new Set());
  const [loadFailed, setLoadFailed] = useState(false);
  const [reloadNonce, setReloadNonce] = useState(0);

  useEffect(() => {
    if (!workspaceId || !projectId) return;
    let cancelled = false;
    setLoadFailed(false);
    Promise.all([
      listAvailableAttributes(workspaceId),
      listProjectAttributes(projectId, workspaceId),
    ])
      .then(([defs, linked]) => {
        if (cancelled) return;
        setAvailable(defs);
        setLinkedIds(new Set(linked.map((d) => d.id)));
      })
      .catch(() => {
        if (cancelled) return;
        setAvailable([]);
        setLinkedIds(new Set());
        setLoadFailed(true);
      });
    return (): void => {
      cancelled = true;
    };
  }, [workspaceId, projectId, reloadNonce]);

  const setLinked = useCallback((definitionId: string, linked: boolean): void => {
    setLinkedIds((prev) => {
      const next = new Set(prev);
      if (linked) next.add(definitionId);
      else next.delete(definitionId);
      return next;
    });
  }, []);

  const toggleColumn = useCallback(
    async (definitionId: string, visible: boolean) => {
      if (!workspaceId || !projectId) return;
      try {
        if (visible) await linkAttributeToProject(projectId, definitionId, workspaceId);
        else await unlinkAttributeFromProject(projectId, definitionId, workspaceId);
        setLinked(definitionId, visible);
      } catch {
        toast.error(
          visible
            ? 'Could not add this field to the project. Try again.'
            : 'Could not remove this field from the project. Try again.',
        );
      }
    },
    [workspaceId, projectId, setLinked],
  );

  const onCreated = useCallback(
    async (definition: AttributeDefinition) => {
      setAvailable((prev) => (prev.some((d) => d.id === definition.id) ? prev : [...prev, definition]));
      if (!workspaceId || !projectId) return;
      try {
        await linkAttributeToProject(projectId, definition.id, workspaceId);
        setLinked(definition.id, true);
      } catch {
        toast.error('The field was created but could not be added to this project. Add it from the list.');
      }
    },
    [workspaceId, projectId, setLinked],
  );

  const retry = useCallback(() => setReloadNonce((n) => n + 1), []);

  // Locked fields are always columns and cannot be removed (TR-2026-11-04-2); the rest follow the server's link.
  const visibleIds = useMemo(() => {
    const ids = new Set(linkedIds);
    for (const d of available) if (d.locked) ids.add(d.id);
    return ids;
  }, [available, linkedIds]);

  const visibleDefinitions = useMemo(
    () => available.filter((d) => visibleIds.has(d.id)),
    [available, visibleIds],
  );

  return { available, visibleIds, visibleDefinitions, loadFailed, toggleColumn, onCreated, retry };
}
